"use client";

import { useEffect, useId, useRef } from "react";
import SettingsScreen from "@/components/SettingsScreen";
import { useFocusTrap } from "@/lib/useFocusTrap";

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function SettingsSheet({ open, onClose }: Props) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  // Trap Tab/Shift+Tab inside the sheet, Esc closes, focus returns
  // to the gear icon (or whichever button opened the sheet) on close.
  useFocusTrap(open, sheetRef, { onEscape: onClose });

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        data-testid="settings-sheet-backdrop"
        className="fixed inset-0 z-50 bg-black/30 transition-opacity lg:left-60"
        onClick={onClose}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        data-testid="settings-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fixed bottom-0 left-0 right-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-3xl bg-cream animate-slide-up lg:bottom-6 lg:left-auto lg:right-6 lg:top-6 lg:w-[28rem] lg:max-h-none lg:rounded-[28px] lg:border lg:border-border lg:shadow-2xl"
      >
        {/* Drag handle */}
        <div className="sticky top-0 z-10 flex justify-center rounded-t-3xl bg-cream pt-3 pb-1 lg:rounded-t-[28px]">
          <div className="w-9 h-1 rounded-full bg-border" />
        </div>

        {/* Visually hidden heading — gives the dialog an accessible
            name for `aria-labelledby` without competing with the
            page-level <h1> rendered inside SettingsScreen. */}
        <h2 id={titleId} className="sr-only">
          Settings
        </h2>

        {/* Close button */}
        <div className="flex justify-end px-4 -mt-1 mb-1">
          <button
            onClick={onClose}
            aria-label="Close settings"
            className="p-1.5 rounded-lg text-muted hover:text-text hover:bg-secondary/50 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-4 pb-8">
          <SettingsScreen />
        </div>
      </div>
    </>
  );
}
